import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { LOCAL_AUTH_FAILURE } from '../../../core/interceptors/local-auth-failure';

export class MemberStatusRequestError extends Error {
  constructor(readonly reason: 'authentication' | 'forbidden' | 'unavailable' | 'invalid-response' | 'rejected') { super(reason); }
}
export interface MemberStatusRecord extends Record<string, unknown> { id: number; status: number; studioId: number; }
function record(value: unknown): value is Record<string, unknown> { return typeof value === 'object' && value !== null && !Array.isArray(value); }
function parseMember(value: unknown, studioId?: number): MemberStatusRecord {
  if (!record(value)) throw new MemberStatusRequestError('invalid-response');
  // The list endpoint selects studio.id, while the member detail returns studioId.
  const actualStudio = value['studioId'] ?? (record(value['studio']) ? value['studio']['id'] : studioId);
  if (!Number.isInteger(value['id']) || Number(value['id']) <= 0 || !Number.isInteger(value['status'])
    || !Number.isInteger(actualStudio) || (studioId !== undefined && actualStudio !== studioId)) throw new MemberStatusRequestError('invalid-response');
  return { ...value, studioId: actualStudio } as MemberStatusRecord;
}

@Injectable({ providedIn: 'root' })
export class MemberStatusApi {
  private http = inject(HttpClient);
  private async request(path: string, body?: object): Promise<unknown> {
    try {
      const options = { context: new HttpContext().set(LOCAL_AUTH_FAILURE, true) };
      const response = await firstValueFrom((body === undefined ? this.http.get<unknown>(environment.apiBaseUrl + path, options)
        : this.http.post<unknown>(environment.apiBaseUrl + path, body, options)).pipe(timeout(30000)));
      if (!record(response) || typeof response['success'] !== 'boolean') throw new MemberStatusRequestError('invalid-response');
      if (response['success'] !== true) throw new MemberStatusRequestError('rejected');
      return response['result'];
    } catch (error) {
      if (error instanceof MemberStatusRequestError) throw error;
      if (error instanceof HttpErrorResponse && error.status === 401) throw new MemberStatusRequestError('authentication');
      if (error instanceof HttpErrorResponse && error.status === 403) throw new MemberStatusRequestError('forbidden');
      throw new MemberStatusRequestError('unavailable');
    }
  }
  async read(memberId: number): Promise<MemberStatusRecord> {
    const member = parseMember(await this.request(`/member/${memberId}`));
    if (member.id !== memberId) throw new MemberStatusRequestError('invalid-response');
    return member;
  }
  async list(studioId: number): Promise<MemberStatusRecord[]> {
    const result = await this.request(`/studio/${studioId}/member`);
    if (!Array.isArray(result)) throw new MemberStatusRequestError('invalid-response');
    return result.map(value => parseMember(value, studioId));
  }
  async update(body: object): Promise<void> { await this.request('/member', body); }
  async remove(memberId: number): Promise<void> { await this.request(`/member/remove/${memberId}`); }
  async disableHistory(memberId: number, endDate: Date): Promise<void> { await this.request('/member/history/disable', { memberId, endDate }); }
}
